import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PostalCodeService } from '../../features/postal-code/postal-code.service';

import { Address } from '../../domain/address.domain';
import {
  ContactAddress,
  ContactEmailAddress,
  ContactStreetAddress,
  ContactTelephoneAddress,
  Employment,
  EmploymentStatus,
  UpdateType,
  UserProfile
} from '../../domain/user-profile.domain';

@Injectable({
  providedIn: 'root'
})
export class UserProfileUtilsService {
  private readonly translateService = inject(TranslateService);

  static readonly RURAL_ROUTE_TYPE: string = 'RURAL_ROUTE';
  static readonly RURAL_ROUTE: string = 'RR';
  private static readonly CANADA: string = 'canada';

  static isCanadianAddress(anAddress: Address): boolean {
    if (anAddress.country && anAddress.country?.toLowerCase() === UserProfileUtilsService.CANADA) {
      return true;
    }
    return false;
  }

  static isAddressMissing(addresses: Array<ContactStreetAddress>): boolean {
    let addressMissing = false;
    // check if two addresses are there
    if (addresses == null || addresses.length < 2) {
      addressMissing =  true;
    } else {
      let mailingAvailable = false;
      let homeAvailable = false;

      for(const anAddress of addresses) {
        if (anAddress.usageCd === 'MAILING_ADDRESS') {
          mailingAvailable = true;
        } else {
          homeAvailable = true;
        }
        addressMissing = this.isAnyAddressFieldsMissing(anAddress.address);
      }

      // if either home or mailing address are missing return true
      if (!mailingAvailable || !homeAvailable) {
        addressMissing = true;
      }
    }

    return addressMissing;
  }

  static isAnyAddressFieldsMissing(anAddress: Address): boolean {
    const noCityName = !anAddress.cityName;
    const noStreetName = !anAddress.streetName;
    const noStreetNum = !anAddress.streetNum;
    const noTerritory = !anAddress.territory;
    const noPostalCode = !anAddress.postalCode;
    const rrAddress = anAddress.ruralRouteNum;
    const poBoxAddress = anAddress.poBox;
    let addressMissing = false;

    // check if city, postal code, street num, street, province are missing and RR or PO not present
    if ((noStreetName || noStreetNum) && (!rrAddress && !poBoxAddress)) {
      addressMissing = true;
    }

    // check if city, postal code, province are missing and RR or PO are there
    if ((noCityName || noTerritory || noPostalCode) && (rrAddress || poBoxAddress)) {
      addressMissing = true;
    }

    return addressMissing;
  }

  static isPhoneMissing(phones: Array<ContactTelephoneAddress>): boolean {
    if (!phones || phones.length === 0) { return true; }

    for(const aPhone of phones) {
      if (!aPhone?.telephone?.localNum || !aPhone?.telephone?.areaCodeNum) {
        return true;
      }
    }

    return false;
  }

  static isEmailMissing(email: ContactEmailAddress): boolean {
    return !email?.email?.emailTxt;
  }

  /**
   * Remove employmentValidationDisabled parameter from following methods when cleaning up kycEnhancementsEnabled feature flag
   *
   * isEmploymentMissing, isEmploymentValidationEnabled, populateMissingInfo
   */
  static isEmploymentMissing(employment: Employment, employmentValidationDisabled: boolean): boolean {
    if (employment.statusCd === EmploymentStatus.RETIRED || this.isEmploymentValidationDisabled(employment, employmentValidationDisabled)) {
      return false;
    }

    return !employment.employerName ||
           !employment.statusCd ||
           (!employment.occupationTypeCd &&
            employment.statusCd !== EmploymentStatus.STUDENT) ||
           this.isEmployerAddressMissing(employment);
  }

  static isEmploymentValidationDisabled(employment: Employment, employmentValidationDisabled: boolean): boolean {
    return employmentValidationDisabled && (employment.statusCd == EmploymentStatus.UNEMPLOYED ||
      employment.statusCd == EmploymentStatus.HOMEMAKER || employment.statusCd == EmploymentStatus.AT_HOME_PARENT);
  }

  static isEmployerAddressMissing(employment: Employment): boolean {
    if (employment.statusCd === EmploymentStatus.STUDENT) { return false; }

    return !employment.streetAddress || this.isAnyAddressFieldsMissing(employment.streetAddress);
  }

  static populateMissingInfo(userProfile: UserProfile, employmentValidationDisabled: boolean): Array<UpdateType> {
    const missingInfo: Array<UpdateType> = [];
    // check for missing info
    if (this.isAddressMissing(userProfile.contactStreetAddresses)) {
      missingInfo.push(UpdateType.ADDRESS);
    }

    if (this.isPhoneMissing(userProfile.contactTelephoneAddresses)) {
      missingInfo.push(UpdateType.PHONE);
    }

    if (this.isEmailMissing(userProfile.contactEmailAddress)) {
      missingInfo.push(UpdateType.EMAIL);
    }

    if (this.isEmploymentMissing(userProfile.employment, employmentValidationDisabled)) {
      missingInfo.push(UpdateType.EMPLOYMENT);
    }

    return missingInfo;
  }

  formatAddress(anAddress: Address): Array<string> {
    const details: Array<string> = [];

    let addressLine = [];
    addressLine.push(anAddress.unitNum ? anAddress.unitNum + ' -' : '');
    addressLine.push([anAddress.streetNum, anAddress.streetNumSuffixVal].join(''));
    addressLine.push(anAddress?.streetName);
    addressLine.push(anAddress.streetType);
    addressLine.push(anAddress.streetDirection);

    details.push(addressLine.join(' ')?.toUpperCase());

    if (anAddress.poBox) {
      addressLine = [];
      addressLine.push(this.translateService.instant('tdWb.userProfile.utils.PO_BOX'));
      addressLine.push(anAddress.poBox);
      addressLine.push(anAddress?.postalStationTxt ?? '');
      details.push(addressLine.join(' '));
    }

    if (anAddress.ruralRouteTypeCd === UserProfileUtilsService.RURAL_ROUTE_TYPE) {
      addressLine = [];
      addressLine.push(UserProfileUtilsService.RURAL_ROUTE);
      addressLine.push(anAddress?.ruralRouteNum ?? '');
      addressLine.push(anAddress?.postalStationTxt ?? '');
      details.push(addressLine.join(' ')?.toUpperCase());
    }

    if (this.hasCityOrTerritoryOrPostalCode(anAddress)) {
      addressLine = [];
      addressLine.push(anAddress.cityName ?? ' ');
      addressLine.push(anAddress.territory ?? ' ');
      addressLine.push(PostalCodeService.formatCanadianPostalCode(anAddress.postalCode) ?? ' ');
      details.push(addressLine.join(', ')?.toUpperCase());
    }

    details.push(anAddress.country?.toUpperCase() ?? ' ');

    return details;
  }

  private hasCityOrTerritoryOrPostalCode(anAddress: Address): boolean {
    return Boolean(anAddress.cityName) || Boolean(anAddress.territory) || Boolean(anAddress.postalCode);
  }

  static retrieveLastUpdatedDate(contactAddresses: Array<ContactAddress>): Date | null {
    let lastUpdatedDate: Date | null = null;

    if (Array.isArray(contactAddresses) && contactAddresses.length > 0) {
      const updatedTimeArray: Array<number> = contactAddresses
        .map((contactAddress: ContactAddress) => {
          const asOfDateTimestamp = contactAddress.lastUpdate?.asOfDttm;
          return asOfDateTimestamp ? new Date(asOfDateTimestamp).getTime() : undefined;
        })
        .filter((time: number | undefined) => time !== undefined);

      if (updatedTimeArray.length > 0) {
        lastUpdatedDate = new Date(Math.max(...updatedTimeArray));
      }
    }

    return lastUpdatedDate;
  }
}

